import { ChevronUpDownIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import React, { RefObject, useCallback, useEffect, useMemo, useRef, useState, KeyboardEvent } from "react";
import { naturalCompare, rankOptions } from "@aidkitorg/component-library";

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ')
}

export function Dropdown(props: {
  options: { name: string, callback: () => void }[],
  // If no title is presented, we just use an up/down icon.
  title?: string,
  // which corner the dropdown will drop down from
  dropdownOrigin?: 'right' | 'left',
  // whether or not the dropdown is searchable
  searchable?: boolean,
  // whether or not to sort the dropdown items alphabetically
  sorted?: boolean,
  className?: string,
}) {
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [hoveredOptionIndex, setHoveredOptionIndex] = useState<number | null>(null);
  const selectRef: RefObject<HTMLDivElement> = useRef(null);

  const orderedOptions = useMemo(() => {
    return props.sorted ? [...props.options].sort((a, b) => naturalCompare(a.name, b.name)) : props.options;
  }, [props.options, props.sorted]);

  // Best matches first, keeping the incoming order within each rank.
  const filteredOptions = useMemo(() => {
    // rankOptions searches on `label`; this list's display text is `name`.
    return rankOptions(orderedOptions.map(o => ({ ...o, label: o.name })), searchText);
  }, [searchText, orderedOptions]);

  const handleKeyPress = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
    if (open) {
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        const newIndex = hoveredOptionIndex !== null ? hoveredOptionIndex - 1 : -1;
        setHoveredOptionIndex((newIndex + filteredOptions.length) % filteredOptions.length);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        const newIndex = hoveredOptionIndex !== null ? hoveredOptionIndex + 1 : 0;
        setHoveredOptionIndex((newIndex + filteredOptions.length) % filteredOptions.length);
      } else if (e.key === 'Enter' && hoveredOptionIndex !== null) {
        e.preventDefault();
        setSearchText('');
        setHoveredOptionIndex(null);
        filteredOptions[hoveredOptionIndex].callback();
        setOpen(false);
      }
    }
  }, [open, hoveredOptionIndex, filteredOptions]);

  const openDropdown = useCallback(() => {
    setOpen(true);
    selectRef?.current?.focus();
  }, [selectRef]);

  // when our hoveredOptionIndex changes, scroll the active option in the dropdown into view
  useEffect(() => {
    selectRef?.current?.querySelector(".active")?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest', 
      inline: 'start'
    });
  }, [hoveredOptionIndex, selectRef]);

  return <div className={"relative inline-block text-left "+props.className}>
    <button 
      className="inline-flex justify-center w-auto shadow-sm px-3 py-1 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-indigo-500"
      onClick={openDropdown}>
      {props.title || <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />}
    </button>
    <div
      className="w-0 h-0" 
      ref={selectRef} 
      onMouseLeave={() => {setOpen(false); setHoveredOptionIndex(null);}}
      tabIndex={0}
      onKeyDown={handleKeyPress}>
      {open && 
        <div className={`origin-top-${props.dropdownOrigin || 'left'} z-[51] absolute ${props.dropdownOrigin || 'left'}-0 mt-2 min-w-56 w-max rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none`}>
          {props.searchable && 
            <div className="flex items-center pl-3 py-[10px] border-b border-gray-100">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
              <input
                className="text-sm w-full -ml-7 -my-1 pl-8 py-1 bg-transparent rounded-md"
                type='text'
                value={searchText}
                onChange={(event) => {
                  setSearchText(event.target.value)
                }}
                autoFocus
                placeholder="Search"
              />
            </div>
          }
          <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
            {filteredOptions.map((option, i) => {
              return <div key={option.name}>
                <a className={classNames(
                  'hover:bg-gray-100 hover:text-gray-900 text-gray-700',
                  hoveredOptionIndex === i ? 'bg-gray-200 active' : '',
                  'block px-4 py-2 text-sm'
                )}
                onClick={(e) => {
                  e.preventDefault();
                  setHoveredOptionIndex(null);
                  setSearchText('');
                  setOpen(false);
                  option.callback();
                }}
                href="#">
                  {option.name}
                </a>
              </div>
            })
            }
          </div>
        </div>
      }
    </div>
  </div>

}
